// The Offer page's frame (lane B8, S12; DC-201, CR-032, CR-034): *Offer package — {candidate}* with the offer's status
// pill and three route tabs, Reference Check · Compensation · Offer, switched through `?tab=` on the page's own route
// (`#requisitions/<R>/offer/<application>?tab=compensation`). Pure: no route state and no read, so it renders under
// SSR; `OfferPage.tsx` is the routed page around it. An accepted offer reads *Offer Accepted* with its date; this
// page never says Filled — the fill is the requisition's.

import type { ReactNode } from "react";
import {
  OFFER_STATUS, PageHeader, Pill, REFCHECK_LABELS, REQUISITION_STATUS, SCREEN_STATUS, SHELL_PAGE_LABELS, SHELL_TEMPLATES,
  Tabs, formatDate, type Tone,
} from "@/mc";
import type { OfferView } from "./types";

export type OfferPageTab = "reference" | "compensation" | "offer";
const TABS: readonly OfferPageTab[] = ["reference", "compensation", "offer"];

/** The tab the route names (`?tab=`), the Offer tab by default. */
export function tabOf(hash: string): OfferPageTab {
  const query = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
  const named = new URLSearchParams(query).get("tab");
  return (TABS as readonly string[]).includes(named ?? "") ? (named as OfferPageTab) : "offer";
}

const STATUS_WORDS: Readonly<Record<string, { word: string; tone: Tone }>> = {
  comp_recorded: { word: OFFER_STATUS.approved, tone: "info" },
  verbal_extended: { word: OFFER_STATUS.verbalExtended, tone: "info" },
  letter_sent: { word: OFFER_STATUS.writtenSent, tone: "info" },
  negotiating: { word: OFFER_STATUS.negotiation, tone: "warn" },
  declined: { word: OFFER_STATUS.declined, tone: "red" },
  withdrawn: { word: SCREEN_STATUS.withdrawn, tone: "neutral" },
};

/** The header's pill: *Offer Accepted* with its date once accepted, else the chain's current word or the stored
 *  status's word. Never Filled. */
export function OfferStatusPill({ offer }: { offer: OfferView }) {
  if (offer.status === "accepted") {
    const fields = offer.fields as Record<string, string | null | undefined>;
    const at = offer.decision?.kind === "accepted" ? offer.decision.decided_at : fields.accepted_at;
    return (
      <Pill tone="ok">
        {REQUISITION_STATUS.offerAccepted}
        {at ? <>{" · "}{formatDate(at)}</> : null}
      </Pill>
    );
  }
  const known = offer.status ? STATUS_WORDS[offer.status] : undefined;
  if (!known) return null;
  return <Pill tone={known.tone}>{known.word}</Pill>;
}

export interface OfferPageViewProps {
  requisitionId: string;
  applicationId: string;
  offer: OfferView;
  tab: OfferPageTab;
  /** The tabs this reader may open (all three by default): Compensation, who reads the worksheet and not the offer,
   *  sees the Compensation tab alone. */
  tabs?: readonly OfferPageTab[];
  /** The active tab's panel. */
  children: ReactNode;
}

/** The page frame: header, pill and route tabs around the active panel (server-rendered in the tests). */
export function OfferPageView({ requisitionId, applicationId, offer, tab, tabs, children }: OfferPageViewProps) {
  const base = `#requisitions/${requisitionId}/offer/${applicationId}`;
  const items = [
    { key: "reference", label: REFCHECK_LABELS.referenceCheck, href: `${base}?tab=reference`, hs: "offer.tab.reference" },
    { key: "compensation", label: SHELL_PAGE_LABELS.compensation, href: `${base}?tab=compensation`, hs: "offer.tab.compensation" },
    { key: "offer", label: SHELL_PAGE_LABELS.offer, href: `${base}?tab=offer`, hs: "offer.tab.offer" },
  ].filter((item) => !tabs || tabs.includes(item.key as OfferPageTab));
  return (
    <div className="mc-offer-page">
      <PageHeader title={SHELL_TEMPLATES.offerPackage({ candidate: offer.candidate })} pills={<OfferStatusPill offer={offer} />} />
      <Tabs route label={SHELL_TEMPLATES.offerPackage({ candidate: offer.candidate })} items={items} active={tab} />
      {children}
    </div>
  );
}
